import { useState, useCallback, useId } from 'react';
import { Check, FlaskConical, Droplets, Flame, Eye } from 'lucide-react';
import LabBench from '../../LabBench';
import InfoBox from '../../InfoBox';
import { saltFormationColors } from '@/config/experiments/chem1131/experiment6.config';

const stepInfoContent: Record<number, { title: string; content: string; whatHappens?: string; observe?: string[]; equation?: string }> = {
  1: {
    title: 'Testing NaHCO₃ with HCl',
    content: 'Sodium bicarbonate (baking soda) is a weak base that reacts with acids. The carbonate ion (CO32-) or bicarbonate ion (HCO3-) combines with H+ to form unstable carbonic acid, which decomposes.',
    whatHappens: 'When HCl is added to NaHCO₃, the acid provides H+ ions that react with bicarbonate to release CO₂ gas.',
    observe: ['Immediate fizzing/effervescence', 'Bubbles rise through solution', 'This is the same reaction used in baking!'],
    equation: 'NaHCO₃ + HCl -> NaCl + H₂O + CO₂(g)'
  },
  2: {
    title: 'Observing CO₂ Gas Evolution',
    content: 'Carbonic acid (H₂CO₃) is unstable and immediately decomposes into water and carbon dioxide gas. The bubbles you see are CO₂ escaping from solution.',
    whatHappens: 'CO₂ gas bubbles form and rise to the surface. The gas is colorless and odorless.',
    observe: ['Vigorous bubbling indicates reaction', 'Gas is released continuously', 'Bubbling slows as reactant is consumed'],
    equation: 'H₂CO₃ -> H₂O + CO₂(g) (unstable intermediate)'
  },
  3: {
    title: 'Flame Test for CO₂',
    content: 'Carbon dioxide does not support combustion - it lacks oxygen for the fire triangle and is denser than air. This makes it useful as a fire extinguisher.',
    whatHappens: 'A lit taper is lowered into the test tube. The CO₂ layer at the top displaces air and extinguishes the flame.',
    observe: ['Flame goes out in CO₂ atmosphere', 'CO₂ is denser than air', 'This confirms CO₂ presence']
  },
  4: {
    title: 'Flame Extinguishes in CO₂',
    content: 'Combustion requires oxygen. CO₂ molecules do not provide oxygen for burning, and the gas dilutes the available O2 below the concentration needed for combustion.',
    whatHappens: 'The flame immediately goes out when it enters the CO₂-rich atmosphere above the reacting solution.',
    observe: ['Flame extinguishes completely', 'No oxygen available for combustion', 'Classic test for CO₂ presence']
  },
  5: {
    title: 'Comparing Strong vs Weak Acids',
    content: 'Strong acids (H₂SO₄) dissociate completely, providing more H+ ions at once. Weak acids (CH3COOH) only partially dissociate, so they react more slowly.',
    whatHappens: 'Both acids react with Na₂CO₃, but H₂SO₄ produces faster, more vigorous bubbling than acetic acid.',
    observe: ['H₂SO₄: vigorous, rapid reaction', 'CH3COOH: slower, gentler bubbling', 'Same products, different rates'],
    equation: 'Na₂CO₃ + H₂SO₄ -> Na₂SO₄ + H₂O + CO₂\nNa₂CO₃ + 2CH3COOH -> 2NaCH3COO + H₂O + CO₂'
  },
  6: {
    title: 'Observing Reaction Rates',
    content: 'Reaction rate depends on concentration of reactive species. Strong acids provide higher [H+], leading to faster reaction. This demonstrates acid strength affects reaction kinetics.',
    whatHappens: 'Side-by-side comparison shows the dramatic difference in reaction rates between strong and weak acids.',
    observe: ['Strong acid: vigorous bubbling', 'Weak acid: gentle bubbling', 'Both eventually produce same products']
  },
  7: {
    title: 'Recording Observations',
    content: 'The reaction of carbonates with acid is a characteristic test for carbonate compounds. It always produces salt + water + CO₂ regardless of which acid is used.',
    whatHappens: 'You record that all carbonate-acid reactions produce CO₂, confirming this is a general method for salt preparation.',
    observe: ['All tests produced CO₂ gas', 'Flame test confirmed gas identity', 'Strong acids react faster than weak'],
    equation: 'Carbonate + Acid -> Salt + H₂O + CO₂'
  }
};

// Bubbles animation component
function CO2Bubbles({ active, intensity = 'normal' }: { active: boolean; intensity?: 'slow' | 'normal' | 'vigorous' }) {
  if (!active) return null;
  const count = intensity === 'vigorous' ? 12 : intensity === 'slow' ? 4 : 8;
  const speed = intensity === 'vigorous' ? 0.6 : intensity === 'slow' ? 1.5 : 1.0;

  return (
    <g>
      {[...Array(count)].map((_, i) => (
        <circle
          key={i}
          cx={18 + (i % 4) * 5}
          cy={80}
          r={2 + (i % 3)}
          fill="rgba(255,255,255,0.5)"
        >
          <animate
            attributeName="cy"
            values="80;30;80"
            dur={`${speed + (i % 3) * 0.2}s`}
            repeatCount="indefinite"
            begin={`${i * 0.15}s`}
          />
          <animate
            attributeName="opacity"
            values="0.5;0.1;0.5"
            dur={`${speed + (i % 3) * 0.2}s`}
            repeatCount="indefinite"
            begin={`${i * 0.15}s`}
          />
        </circle>
      ))}
    </g>
  );
}

export default function acidCarbonate() {
  const [showInfo, setShowInfo] = useState<number | null>(null);
  const [nahco3Tested, setNahco3Tested] = useState(false);
  const [bubblingObserved, setBubblingObserved] = useState(false);
  const [flameTestDone, setFlameTestDone] = useState(false);
  const [flameExtinguished, setFlameExtinguished] = useState(false);
  const [acidsCompared, setAcidsCompared] = useState(false);
  const [ratesObserved, setRatesObserved] = useState(false);
  const [recorded, setRecorded] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [bubbling, setBubbling] = useState(false);
  const [flameActive, setFlameActive] = useState(false);
  const [comparingAcids, setComparingAcids] = useState(false);
  const id = useId();

  const completedSteps = [nahco3Tested, bubblingObserved, flameTestDone, flameExtinguished, acidsCompared, ratesObserved, recorded].filter(Boolean).length;

  const handleTestNaHCO3 = useCallback(() => {
    if (currentStep === 1 && !nahco3Tested) {
      setNahco3Tested(true);
      setBubbling(true);
      setCurrentStep(2);
    }
  }, [currentStep, nahco3Tested]);

  const handleObserveBubbling = useCallback(() => {
    if (currentStep === 2 && nahco3Tested && !bubblingObserved) {
      setBubblingObserved(true);
      setCurrentStep(3);
    }
  }, [currentStep, nahco3Tested, bubblingObserved]);

  const handleFlameTest = useCallback(() => {
    if (currentStep === 3 && bubblingObserved && !flameTestDone) {
      setFlameTestDone(true);
      setFlameActive(true);
      setCurrentStep(4);
      // Simulate flame extinguishing
      setTimeout(() => {
        setFlameActive(false);
        setFlameExtinguished(true);
        setCurrentStep(5);
      }, 2000);
    }
  }, [currentStep, bubblingObserved, flameTestDone]);

  const handleCompareAcids = useCallback(() => {
    if (currentStep === 5 && flameExtinguished && !acidsCompared) {
      setAcidsCompared(true);
      setComparingAcids(true);
      setCurrentStep(6);
    }
  }, [currentStep, flameExtinguished, acidsCompared]);

  const handleObserveRates = useCallback(() => {
    if (currentStep === 6 && acidsCompared && !ratesObserved) {
      setRatesObserved(true);
      setCurrentStep(7);
    }
  }, [currentStep, acidsCompared, ratesObserved]);

  const handleRecord = useCallback(() => {
    if (currentStep === 7 && ratesObserved && !recorded) {
      setRecorded(true);
    }
  }, [currentStep, ratesObserved, recorded]);

  return (
    <div className="experiment-layout">
      <div className="steps-panel">
        <div className="steps-header">
          <FlaskConical size={16} />
          <h3>Part D: Acid + Carbonate</h3>
        </div>
        <div className="steps-list">
          {[
            { id: 1, title: 'Test NaHCO₃ with HCl', hint: 'Add HCl to NaHCO₃', done: nahco3Tested },
            { id: 2, title: 'Observe Bubbling', hint: 'Watch CO₂ gas evolve', done: bubblingObserved },
            { id: 3, title: 'Flame Test', hint: 'Insert lit taper', done: flameTestDone },
            { id: 4, title: 'Observe Flame Extinguish', hint: 'Confirm CO₂ present', done: flameExtinguished },
            { id: 5, title: 'Compare Acids', hint: 'H₂SO₄ vs acetic acid', done: acidsCompared },
            { id: 6, title: 'Observe Rates', hint: 'Compare reaction speeds', done: ratesObserved },
            { id: 7, title: 'Record', hint: 'Record observations', done: recorded }
          ].map((step) => (
            <div
              key={step.id}
              className={`step-item ${step.done ? 'completed' : ''} ${currentStep === step.id ? 'active' : ''}`}
            >
              <div className={`step-number ${step.done ? 'done' : ''}`}>
                {step.done ? <Check size={12} /> : step.id}
              </div>
              <div className="step-content">
                <span className="step-title">{step.title}</span>
                {currentStep === step.id && !step.done && (
                  <span className="step-hint">{step.hint}</span>
                )}
              </div>
              {stepInfoContent[step.id] && (
                <button
                  className="step-info-btn"
                  onClick={(e) => { e.stopPropagation(); setShowInfo(step.id); }}
                >
                  ?
                </button>
              )}
            </div>
          ))}
        </div>
      </div>

      <LabBench
        experimentName="Carbonate + Acid Reaction"
        completedCount={completedSteps}
        totalSteps={7}
        equipment={['NaHCO₃', 'Na₂CO₃', 'HCl', 'H₂SO₄', 'Acetic acid', 'Taper']}
        labMode="guided"
        currentStep={currentStep}
      >
        <div className="reaction-workspace">
          <div className="equipment-row">
            {/* NaHCO₃ + HCl Test Tube */}
            <div
              className={`equipment-station ${(currentStep === 1 || currentStep === 2) ? 'clickable' : ''}`}
              onClick={() => {
                if (currentStep === 1) handleTestNaHCO3();
                else if (currentStep === 2) handleObserveBubbling();
              }}
            >
              <svg viewBox="0 0 60 130" width={55} height={125}>
                <defs>
                  <linearGradient id={`tube-${id}`} x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="rgba(200,220,240,0.6)" />
                    <stop offset="50%" stopColor="rgba(255,255,255,0.3)" />
                    <stop offset="100%" stopColor="rgba(200,220,240,0.5)" />
                  </linearGradient>
                </defs>

                {/* Tube rim */}
                <ellipse cx="30" cy="15" rx="14" ry="5" fill="rgba(200,220,240,0.5)" stroke="#8BA8C4" strokeWidth="1.5" />

                {/* Tube body */}
                <path
                  d="M16 15 L16 90 Q16 105 30 105 Q44 105 44 90 L44 15"
                  fill={`url(#tube-${id})`}
                  stroke="#8BA8C4"
                  strokeWidth="1.5"
                />

                {/* Solution */}
                {nahco3Tested && (
                  <path
                    d="M18 50 L18 88 Q18 100 30 100 Q42 100 42 88 L42 50 Z"
                    fill="rgba(255,255,255,0.8)"
                  />
                )}

                {/* Powder (before acid) */}
                {!nahco3Tested && (
                  <ellipse cx="30" cy="95" rx="10" ry="4" fill={saltFormationColors.nahco3} />
                )}

                {/* Bubbles */}
                <CO2Bubbles active={bubbling} intensity="normal" />

                {/* Glass shine */}
                <line x1="19" y1="25" x2="19" y2="80" stroke="rgba(255,255,255,0.5)" strokeWidth="2" strokeLinecap="round" />

                <text x="30" y="120" fontSize="8" fill="#E0E0E0" textAnchor="middle">NaHCO₃</text>
              </svg>
            </div>

            {/* Flame test taper */}
            <div
              className={`equipment-station ${currentStep === 3 ? 'clickable' : ''}`}
              onClick={handleFlameTest}
            >
              <svg viewBox="0 0 50 100" width={45} height={95}>
                {/* Taper stick */}
                <rect x="23" y="30" width="4" height="60" fill="#8B4513" />

                {/* Flame */}
                {flameActive && (
                  <g>
                    <ellipse cx="25" cy="25" rx="8" ry="15" fill="url(#taper-flame)">
                      <animate attributeName="ry" values="15;17;13;15" dur="0.2s" repeatCount="indefinite" />
                    </ellipse>
                    <ellipse cx="25" cy="22" rx="4" ry="8" fill="#FFD700" opacity="0.9">
                      <animate attributeName="ry" values="8;10;7;8" dur="0.15s" repeatCount="indefinite" />
                    </ellipse>
                    <defs>
                      <linearGradient id="taper-flame" x1="0%" y1="100%" x2="0%" y2="0%">
                        <stop offset="0%" stopColor={saltFormationColors.outerFlame} />
                        <stop offset="50%" stopColor="#FFFF00" />
                        <stop offset="100%" stopColor="#FFFF00" stopOpacity="0" />
                      </linearGradient>
                    </defs>
                  </g>
                )}

                {/* Extinguished smoke */}
                {flameExtinguished && !flameActive && (
                  <g>
                    {[...Array(3)].map((_, i) => (
                      <ellipse
                        key={i}
                        cx={25 + i * 2}
                        cy={20}
                        rx={3}
                        ry={4}
                        fill="rgba(128,128,128,0.4)"
                      >
                        <animate attributeName="cy" values="20;5" dur="1.5s" fill="freeze" />
                        <animate attributeName="opacity" values="0.4;0" dur="1.5s" fill="freeze" />
                      </ellipse>
                    ))}
                  </g>
                )}

                <text x="25" y="98" fontSize="7" fill="#E0E0E0" textAnchor="middle">
                  {flameExtinguished ? 'Extinguished!' : 'Taper'}
                </text>
              </svg>
            </div>

            {/* Acid comparison tubes */}
            <div
              className={`equipment-station ${(currentStep === 5 || currentStep === 6) ? 'clickable' : ''}`}
              onClick={() => {
                if (currentStep === 5) handleCompareAcids();
                else if (currentStep === 6) handleObserveRates();
              }}
            >
              <div className="comparison-tubes">
                {/* H₂SO₄ + Na₂CO₃ */}
                <svg viewBox="0 0 50 110" width={45} height={105}>
                  <defs>
                    <linearGradient id={`tube-h2so4-${id}`} x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" stopColor="rgba(200,220,240,0.6)" />
                      <stop offset="50%" stopColor="rgba(255,255,255,0.3)" />
                      <stop offset="100%" stopColor="rgba(200,220,240,0.5)" />
                    </linearGradient>
                  </defs>

                  <ellipse cx="25" cy="12" rx="12" ry="4" fill="rgba(200,220,240,0.5)" stroke="#8BA8C4" strokeWidth="1.5" />
                  <path
                    d="M13 12 L13 80 Q13 95 25 95 Q37 95 37 80 L37 12"
                    fill={`url(#tube-h2so4-${id})`}
                    stroke="#8BA8C4"
                    strokeWidth="1.5"
                  />

                  {/* Contents */}
                  {acidsCompared && (
                    <path
                      d="M15 45 L15 78 Q15 90 25 90 Q35 90 35 78 L35 45 Z"
                      fill="rgba(255,255,255,0.8)"
                    />
                  )}

                  {/* Vigorous bubbles for H₂SO₄ */}
                  <CO2Bubbles active={comparingAcids} intensity="vigorous" />

                  <text x="25" y="108" fontSize="6" fill="#E0E0E0" textAnchor="middle">H₂SO₄</text>
                  {comparingAcids && (
                    <text x="25" y="40" fontSize="5" fill="#FF5A36" textAnchor="middle">Vigorous!</text>
                  )}
                </svg>

                {/* CH3COOH + Na₂CO₃ */}
                <svg viewBox="0 0 50 110" width={45} height={105}>
                  <defs>
                    <linearGradient id={`tube-acetic-${id}`} x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" stopColor="rgba(200,220,240,0.6)" />
                      <stop offset="50%" stopColor="rgba(255,255,255,0.3)" />
                      <stop offset="100%" stopColor="rgba(200,220,240,0.5)" />
                    </linearGradient>
                  </defs>

                  <ellipse cx="25" cy="12" rx="12" ry="4" fill="rgba(200,220,240,0.5)" stroke="#8BA8C4" strokeWidth="1.5" />
                  <path
                    d="M13 12 L13 80 Q13 95 25 95 Q37 95 37 80 L37 12"
                    fill={`url(#tube-acetic-${id})`}
                    stroke="#8BA8C4"
                    strokeWidth="1.5"
                  />

                  {/* Contents */}
                  {acidsCompared && (
                    <path
                      d="M15 45 L15 78 Q15 90 25 90 Q35 90 35 78 L35 45 Z"
                      fill="rgba(255,255,255,0.8)"
                    />
                  )}

                  {/* Slow bubbles for acetic acid */}
                  <CO2Bubbles active={comparingAcids} intensity="slow" />

                  <text x="25" y="108" fontSize="6" fill="#E0E0E0" textAnchor="middle">CH3COOH</text>
                  {comparingAcids && (
                    <text x="25" y="40" fontSize="5" fill="#60A5FA" textAnchor="middle">Slow</text>
                  )}
                </svg>
              </div>
            </div>
          </div>

          {/* Status Messages */}
          <div className="status-area">
            {currentStep === 1 && !nahco3Tested && (
              <div className="status-message info">
                <Droplets size={20} />
                Click tube to add HCl to NaHCO₃ (baking soda)
              </div>
            )}
            {currentStep === 2 && !bubblingObserved && (
              <div className="status-message info">
                <Eye size={20} />
                Click to observe CO₂ gas bubbles evolving
              </div>
            )}
            {currentStep === 3 && !flameTestDone && (
              <div className="status-message info">
                <Flame size={20} />
                Click taper to test gas - flame will extinguish in CO₂
              </div>
            )}
            {flameActive && (
              <div className="status-message reaction">
                <Flame size={20} />
                Flame entering the tube... CO₂ does not support combustion
              </div>
            )}
            {currentStep === 5 && flameExtinguished && !acidsCompared && (
              <div className="status-message info">
                <FlaskConical size={20} />
                Click to compare H₂SO₄ (strong) vs acetic acid (weak)
              </div>
            )}
            {currentStep === 6 && !ratesObserved && (
              <div className="status-message info">
                <Eye size={20} />
                Click to observe: H₂SO₄ reacts vigorously, acetic acid slowly
              </div>
            )}
            {currentStep === 7 && !recorded && (
              <div
                className="status-message success"
                onClick={handleRecord}
                style={{ cursor: 'pointer' }}
              >
                <Check size={20} />
                Click to record: Strong acids react faster than weak acids
              </div>
            )}
            {recorded && (
              <div className="status-message success">
                <Check size={20} />
                Complete! Carbonates + acid = salt + water + CO₂
              </div>
            )}
          </div>

          {/* Theory Box */}
          <div className="theory-box">
            <h4>Carbonate + Acid Reaction</h4>
            <div className="equations">
              <div className="equation">NaHCO₃ + HCl → NaCl + H₂O + CO₂</div>
              <div className="equation">Na₂CO₃ + 2HCl → 2NaCl + H₂O + CO₂</div>
            </div>
            <p>
              Carbonates react with acids to release CO₂ gas. The gas extinguishes flames
              because it doesn't support combustion. Strong acids (H₂SO₄) react faster
              than weak acids (CH3COOH).
            </p>
          </div>
        </div>

        <style>{`
          .reaction-workspace {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 24px;
            padding: 20px;
            width: 100%;
          }

          .equipment-row {
            display: flex;
            gap: 40px;
            align-items: flex-end;
            justify-content: center;
          }

          .equipment-station {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 8px;
            padding: 10px;
            border-radius: 8px;
            transition: all 0.2s ease;
          }

          .equipment-station.clickable {
            cursor: pointer;
          }

          .equipment-station.clickable:hover {
            background: rgba(0, 168, 107, 0.1);
          }

          .comparison-tubes {
            display: flex;
            gap: 10px;
          }

          .status-area {
            min-height: 50px;
            display: flex;
            justify-content: center;
          }

          .status-message {
            display: flex;
            align-items: center;
            gap: 10px;
            padding: 12px 20px;
            border-radius: 8px;
            font-size: 14px;
            font-weight: 500;
          }

          .status-message.info {
            background: rgba(59, 130, 246, 0.2);
            color: #60A5FA;
          }

          .status-message.reaction {
            background: rgba(255, 90, 54, 0.2);
            color: #FF5A36;
          }

          .status-message.success {
            background: rgba(0, 168, 107, 0.2);
            color: var(--secondary);
          }

          .theory-box {
            background: var(--bg-elevated);
            border-radius: 8px;
            padding: 16px;
            max-width: 500px;
            border-left: 3px solid var(--secondary);
          }

          .theory-box h4 {
            margin: 0 0 8px 0;
            color: var(--secondary);
            font-size: 14px;
          }

          .equations {
            background: rgba(0, 0, 0, 0.3);
            padding: 8px 12px;
            border-radius: 4px;
            margin-bottom: 8px;
          }

          .equation {
            font-family: 'Courier New', monospace;
            color: #40E0D0;
            padding: 4px 0;
            font-size: 12px;
          }

          .theory-box p {
            margin: 0;
            font-size: 13px;
            color: var(--text-secondary);
            line-height: 1.5;
          }
        .step-info-btn {
          width: 18px;
          height: 18px;
          border-radius: 50%;
          background: var(--accent);
          color: var(--bg-dark);
          font-size: 11px;
          font-weight: 700;
          border: none;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          transition: transform 0.15s;
        }
        .step-info-btn:hover {
          transform: scale(1.1);
        }
        `}</style>
      </LabBench>

      {showInfo && stepInfoContent[showInfo] && (
        <InfoBox
          step={showInfo}
          experimentId="exp6"
          stepInfo={stepInfoContent[showInfo]}
          onClose={() => setShowInfo(null)}
        />
      )}
    </div>
  );
}
